import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { useGetPostsQuery } from 'store/api/configService/postController';
import { viewModeSelector } from 'store/slices/viewMode';

import { findPathPages } from 'utils/pages';
import { VIEW_MODE } from 'utils/viewMode';

import { Skeleton } from '@mui/material';

import AnimationLayout from 'components/Layouts/Animation/AnimationLayout';
import HomeComponentsLayout from 'components/Layouts/HomeComponents/HomeComponentsLayout';
import Item from 'components/pages/Home/News/Item/Item';
import Main from 'components/pages/Home/News/Main/Main';

import { categories } from 'components/config/newsAndUpdates';
import { PAGE_PATH } from 'components/config/pages';

import styles from './News.module.scss';

const itemsSizes = {
  [VIEW_MODE.SIMPLE]: 4,
  [VIEW_MODE.WIDE]: 5,
};

const News = () => {
  const viewMode = useSelector(viewModeSelector.getViewMode);
  const isWideMode = useSelector(viewModeSelector.isWide);

  const { data, isLoading } = useGetPostsQuery({
    size: itemsSizes[viewMode],
  });

  const news = useMemo(
    () =>
      data?.content?.map((post) => ({
        ...post,
        category:
          post.postType === categories.news.postType ? 'news' : 'updates',
      })),
    [data],
  );

  const mainItem = useMemo(() => news?.[0], [news]);

  if (!isLoading && !news?.length) return null;

  return (
    <HomeComponentsLayout
      linkPath={findPathPages(PAGE_PATH.NEWS)}
      title="News and Updates"
      linkTitle="All News"
    >
      {!isLoading ? (
        <AnimationLayout animation="fadeInUp">
          <div className={styles.wrapper}>
            <Main news={mainItem} />
            <div className={styles.newsItems}>
              {news.slice(1).map((item) => (
                <Item key={item.id} {...item} />
              ))}
            </div>
          </div>
        </AnimationLayout>
      ) : (
        <div className={styles.skeletons}>
          <div className={styles.skeletonsMain}>
            <Skeleton variant="rounded" height={isWideMode ? 480 : 310} />
            <Skeleton />
            <Skeleton width="20%" />
          </div>
          <div className={styles.skeletonsItems}>
            {Array(itemsSizes[viewMode] - 1)
              .fill(null)
              .map((_, index) => (
                <div key={index} className={styles.skeletonsItem}>
                  <Skeleton variant="rounded" height={115} />
                  <div className={styles.skeletonsItemText}>
                    <Skeleton />
                    <Skeleton width="30%" />
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </HomeComponentsLayout>
  );
};

export default News;
